import { yupResolver } from '@hookform/resolvers/yup';
import DoneIcon from '@mui/icons-material/Done';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import UploadCustomDocumentModal from '@features/digitalOnboarding/components/UploadCustomDocumentModal/UploadCustomDocumentModal';
import { useGetTemplates } from '@features/digitalOnboarding/hooks/useGetTemplates';
import { useGetJobPostings } from '@features/jobPostings/hooks/useGetJobPostings';
import { useAddOnboardingPackage } from '@features/digitalOnboarding/hooks/useAddOnboardingPackage';
import { toast } from 'react-toastify';
import { Close, Upload } from '@mui/icons-material';
import { OnboardingPackage } from '@features/digitalOnboarding/types';
import { AddPlusIcon, EditIcon } from '../../../../vectors/index';
import { authContext, Position } from '@staffedupusa/shared-components';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { selectedPlanName } from '@utils/pricing';
import { getStrippedText } from '@utils/helper';

type AddOnboardingPackageModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  selectedCompanyId?: number | null;
  onboardingPackageData: OnboardingPackage | null;
};

type FormData = {
  package_name: string;
  joblisting_ids: string[];
  template_ids: string[] | number[];
};

const schema: yup.ObjectSchema<FormData> = yup
  .object({
    package_name: yup
      .string()
      .required('Package Name is Required')
      .test('not-empty-html', 'Package Name cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    template_ids: yup
      .array()
      .of(yup.string())
      .required('Documents are required')
      .test(
        'not-empty',
        'Documents cannot be empty',
        (value) =>
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((v) => typeof v === 'string' && v.trim() !== ''),
      ),
    joblisting_ids: yup
      .array()
      .of(yup.string())
      .required('Positions are required')
      .test(
        'not-empty',
        'Positions cannot be empty',
        (value) =>
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((v) => typeof v === 'string' && v.trim() !== ''),
      ),
  })
  .required();

const tooltipText =
  'Server Onboarding, Front of House Onboarding, All staff Onboarding, etc.';

const OnboardingPackageModal: FC<AddOnboardingPackageModalProps> = ({
  isOpen,
  handleClose,
  selectedCompanyId,
  onboardingPackageData,
}) => {
  const theme = useTheme();
  const { authData } = useContext(authContext);
  const isCompamy = authData?.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const [errorText, setErrorText] = useState<string>('');
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
    useState(false);

  const { jobPostingsData, isLoadingJobPostings } =
    useGetJobPostings(selectedCompanyId);
  const { templatesData, isLoadingTemplates } = useGetTemplates();
  const [defaultValues, setDefaultValues] = useState<FormData>({
    package_name: '',
    template_ids: [],
    joblisting_ids: [],
  });

  const allOptionsDisabled =
    jobPostingsData &&
    jobPostingsData?.object?.every(
      (option: { is_assigned: any }) => option.is_assigned,
    );
  const { addOnboardPackage, isAddingOnboardPackage } = useAddOnboardingPackage(
    {
      setErrorText,
    },
  );

  useEffect(() => {
    if (onboardingPackageData) {
      setDefaultValues({
        package_name: onboardingPackageData.package_name || '',
        template_ids:
          onboardingPackageData.templates.map((item) => item.id) || [],
        joblisting_ids:
          onboardingPackageData.positions
            .filter((item: any) => item.status !== 4)
            .map((item) => item.id) || [],
      });
    } else {
      setDefaultValues({
        package_name: '',
        template_ids: [],
        joblisting_ids: [],
      });
    }
  }, [onboardingPackageData]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues,
  });
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = (values: FormData) => {
    isCompamy &&
      track(
        `${amplitudeEventsName.ONBOARDING_CLICK_SAVE_ONBOARDING_PACKAGE}-${selectedPlan}`,
      );
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          toast.success(data.message);
          reset();
          handleClose();
        }
      },
    };
    const additionalData = onboardingPackageData
      ? { is_update: 1, package_id: onboardingPackageData.id }
      : {};

    const formattedValues = {
      ...values,
      joblisting_ids: values.joblisting_ids.join(','),
      ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
      template_ids: values.template_ids.join(','),
    };

    addOnboardPackage({ ...formattedValues, ...additionalData }, options);
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
          <br />
          {onboardingPackageData ? <EditIcon /> : <AddPlusIcon />}
          <br />
          <Typography variant="h4">
            {onboardingPackageData ? 'Update' : 'Add'} Onboarding Package Choose
            Onboarding Documents
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginBottom: 3 }}>
            {errorText && <Alert severity="error">{errorText}</Alert>}
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack
                spacing={3}
                sx={{
                  pt: 1,
                  '& p': {
                    ml: 1,
                    fontSize: '15px',
                  },
                }}
              >
                <Controller
                  control={control}
                  name="package_name"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={Boolean(errors.package_name)}
                      onFocus={() => {
                        isCompamy &&
                          track(
                            `${amplitudeEventsName.ONBOARDING_CLICK_FIELD_NAME}-${selectedPlan}`,
                          );
                      }}
                      helperText={
                        errors?.package_name ? errors?.package_name.message : ''
                      }
                      InputProps={{
                        endAdornment: (
                          <Tooltip
                            title={tooltipText}
                            placement="top"
                            arrow
                            sx={{ cursor: 'pointer' }}
                          >
                            <HelpOutlineIcon
                              color="secondary"
                              fontSize="small"
                            />
                          </Tooltip>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="template_ids"
                  render={({ field: { onChange, value } }) => {
                    const allTemplateIds =
                      templatesData?.object.map(
                        (item: { id: string }) => item.id,
                      ) || [];
                    const isAllSelected =
                      value.length === allTemplateIds.length;
                    return (
                      <Box display="flex" alignItems="center">
                        <Autocomplete
                          sx={{
                            flexGrow: 1,
                            bgcolor: 'white',
                            borderRadius: (theme) =>
                              `${theme.shape.borderRadius}px`,
                          }}
                          options={[
                            {
                              id: 'select_all',
                              name: isAllSelected
                                ? 'Deselect All'
                                : 'Select All',
                            },
                            ...(templatesData?.object || []),
                          ]}
                          value={
                            templatesData?.object.filter(
                              (template: { id: string }) =>
                                (value as string[]).includes(template.id),
                            ) || []
                          }
                          onChange={(e, data) => {
                            isCompamy &&
                              track(
                                `${amplitudeEventsName.ONBOARDING_CLICK_ADD_DOCUMENT}-${selectedPlan}`,
                              );
                            if (data.some((item) => item.id === 'select_all')) {
                              onChange(isAllSelected ? [] : allTemplateIds);
                            } else {
                              onChange(data.map((item) => item.id));
                            }
                          }}
                          onFocus={() => {
                            isCompamy &&
                              track(
                                `${amplitudeEventsName.ONBOARDING_CLICK_SELECT_DOCUMENT}-${selectedPlan}`,
                              );
                          }}
                          multiple
                          loading={isLoadingTemplates}
                          getOptionLabel={(option) => option.name || ''}
                          noOptionsText={
                            <Stack spacing={2} alignItems="center">
                              <Typography variant="subtitle1">
                                No options
                              </Typography>
                              <Button
                                variant="contained"
                                onClick={() => {
                                  setIsUploadDocumentModalOpen(true);
                                }}
                              >
                                Upload document
                              </Button>
                            </Stack>
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Documents"
                              variant="outlined"
                              size="small"
                              fullWidth
                              error={Boolean(errors.template_ids)}
                              helperText={
                                errors?.template_ids
                                  ? errors?.template_ids.message
                                  : ''
                              }
                            />
                          )}
                        />
                        <IconButton
                          onClick={() => {
                            isCompamy &&
                              track(
                                `${amplitudeEventsName.ONBOARDING_CLICK_UPLOAD_DOCUMENT}-${selectedPlan}`,
                              );
                            setIsUploadDocumentModalOpen(true);
                          }}
                          sx={{ ml: 1 }}
                        >
                          <Upload />
                        </IconButton>
                      </Box>
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="joblisting_ids"
                  render={({ field: { onChange, value } }) => {
                    const allJobIds =
                      jobPostingsData?.object
                        ?.filter((item: any) => item.status !== 4 && !item.is_assigned)
                        ?.map((job: Position) => job.id) || [];
                    const isAllSelected = value.length === allJobIds.length;
                    return (
                      <Autocomplete
                        sx={{
                          bgcolor: 'white',
                          borderRadius: `${theme.shape.borderRadius}px`,
                        }}
                        getOptionDisabled={(option) => option.is_assigned}
                        options={[
                          {
                            id: 'select_all',
                            s_title: isAllSelected
                              ? 'Deselect All'
                              : 'Select All',
                          },
                          ...(jobPostingsData?.object?.filter(
                            (item: any) => item.status !== 4,
                          ) || []),
                        ]}
                        value={
                          jobPostingsData?.object.filter(
                            (job: { id: string }) => value.includes(job.id),
                          ) || []
                        }
                        onChange={(e, data) => {
                          isCompamy &&
                            track(
                              `${amplitudeEventsName.ONBOARDING_CLICK_ADD_POSITION}-${selectedPlan}`,
                            );
                          if (data.some((item) => item.id === 'select_all')) {
                            onChange(isAllSelected ? [] : allJobIds);
                          } else {
                            onChange(data.map((item) => item.id));
                          }
                        }}
                        onFocus={() => {
                          isCompamy &&
                            track(
                              `${amplitudeEventsName.ONBOARDING_CLICK_SELECT_POSITION}-${selectedPlan}`,
                            );
                        }}
                        multiple
                        loading={isLoadingJobPostings}
                        getOptionLabel={(option) => option.s_title || ''}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderOption={(props, option) => {
                          const isDisabled = option.is_assigned;
                          return (
                            <Tooltip
                              title={
                                isDisabled
                                  ? 'This Position is already assigned to another package'
                                  : ''
                              }
                              arrow
                              disableInteractive
                            >
                              <div style={{ width: '100%' }}>
                                <ListItem
                                  {...props}
                                  style={{
                                    opacity: isDisabled ? 0.5 : 1,
                                    pointerEvents: isDisabled ? 'none' : 'auto',
                                  }}
                                  disabled={isDisabled}
                                >
                                  <ListItemText primary={option.s_title} />
                                </ListItem>
                              </div>
                            </Tooltip>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Positions"
                            variant="outlined"
                            size="small"
                            fullWidth
                            error={Boolean(errors.joblisting_ids)}
                            helperText={
                              errors?.joblisting_ids
                                ? errors?.joblisting_ids.message
                                : ''
                            }
                          />
                        )}
                      />
                    );
                  }}
                />
                {allOptionsDisabled && !onboardingPackageData && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ marginBottom: 2 }}
                  >
                    Please make at least one position available in default/1st
                    package in order to assign positions to another package.
                  </Typography>
                )}
                <LoadingButton
                  type="submit"
                  startIcon={<DoneIcon />}
                  variant="contained"
                  size="small"
                  fullWidth
                  disabled={allOptionsDisabled && !onboardingPackageData}
                  loading={isAddingOnboardPackage}
                  loadingPosition="start"
                >
                  Save Package
                </LoadingButton>
              </Stack>
            </form>
          </Stack>
        </DialogContent>
      </Dialog>
      <UploadCustomDocumentModal
        isOpen={isUploadDocumentModalOpen}
        handleClose={() => setIsUploadDocumentModalOpen(false)}
        selectedCompanyId={selectedCompanyId}
      />
    </>
  );
};

export default OnboardingPackageModal;
